import React, { useEffect, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'next-i18next/pages'
import { FullLanguages } from 'constants/constants'
import { PersonalDataInputs } from 'store/customer/types'
import { FlowAlternatives, InputGroupKeys, MeterDetailsFieldKeys, PersonalDataFieldKeys, SimulationFieldKeys } from 'store/customer/enums'
import FormButtons from '../../components/form-buttons/FormButtons'
import { useRouter } from 'next/router'
import { EventTrackingParameters } from 'types/event-tracking-parameters'
import { useBeforeunload } from 'react-beforeunload'
import { Language } from 'types/language'
import AlreadyCustomerModal from 'features/registration/components/already-customer-modal/AlreadyCustomerModal'
import { checkIsEavOrFixed } from 'utils/customerFlow'
import { determineSimulationUsage } from 'features/simulation/utils'
import { updateAccountId, updateOpportunityId } from 'store/app/slice'
import { setExtraAddressAccountsByEmail, setIsExtraAddressFlow, updateAllInputs, updateInput } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { Address } from 'types/customer'
import { identifyUser, setPostHogPersonProperties, trackEvent, trackGTMEvent } from 'utils/tracking'
import { addLogBreadcrumb, log, setLogContext, setLogUser } from 'utils/logging'
import { Criticality, Flow, Severity } from 'types/logging'
import { Banner, Checkbox, Form, Select } from '@boltenergy-be/design-system'
import { CompanyName, CompanyNumber, Email, FirstName, LastName, Phone } from 'components/inputs'
import axios from 'axios'
import { CustomerExistsResponse, NextApiResponseBody } from 'types/bolt-api/request'
import { EanLookupResponseBody } from 'types/contract'
import { selectIsBoltDriveFlexFlow } from 'features/bolt-drive-flex/selectors'
import { StepProps } from 'features/registration/steps/types'
import {
  CUSTOMER_TYPES,
  EAN_CAPTURE_TYPES,
  ERROR_CODES,
  IDENTIFICATION_METHODS,
  LEGAL_FORMS,
  REGIONS,
  SIMULATION_TYPES,
  USAGE_GRADES
} from '@boltenergy-be/shared'
import boltBackend from 'lib/bolt-backend'
import { parseCreateProspectData } from 'lib/bolt-backend/registrations/upsertProspect.utils'
import ExtraAddressModal from 'features/registration/components/extra-address-modal/ExtraAddressModal'
import { checkAndHandleSalesUser } from 'utils/sales'
import { partnersLib } from 'lib/index'
import BirthDate from 'components/inputs/birth-date/BirthDate'
import dayjs from 'dayjs'
import { convertDisplayDateToISO } from 'utils/dayjs'
import { CommonTrackingParams } from 'types/tracking'
import { updateMarketingData } from 'store/marketing/slice'
import { MarketingDataKeys } from 'store/marketing/types'
import { checkProductIsAvailableInRegion } from 'utils/products'

const PersonalData = ({ nextStep, producer, saveAndResimulate, producerOverCapacity }: StepProps) => {
  // Redux store
  const marketingStore = useStoreSelector((store) => store.marketing)
  const {
    emailConfirmationFlow,
    executeCreditCheck,
    noEanFlow,
    creditCheckConfirmationFlow,
    partnerID,
    identificationMethod,
    availableProducts
  } = marketingStore
  const isBoltDriveFlexFlow = selectIsBoltDriveFlexFlow(marketingStore)
  const { fixedPrices, inputs, propositions, extraAddress, flowAlternative, itsme } = useStoreSelector((store) => store.customer)
  const { personalData, simulation, deliveryAddress } = inputs
  const dispatch = useStoreDispatch()

  // Next router
  const { locale } = useRouter()

  // React hook form
  const hookForm = useForm<PersonalDataInputs>({
    defaultValues: {
      ...personalData,
      ...(personalData.isCompany && {
        legalForm: 'legalForm' in personalData ? personalData.legalForm : LEGAL_FORMS.srl_bv,
        subjectToVat: 'subjectToVat' in personalData ? personalData.subjectToVat : true
      }),
      dateOfBirth: personalData.dateOfBirth ? dayjs(personalData.dateOfBirth).format('DD/MM/YYYY') : ''
    },
    mode: 'onBlur'
  })
  const {
    formState: { errors, isDirty },
    handleSubmit,
    register,
    setValue,
    control
  } = hookForm
  const watchCompanyNumber = useWatch({ control, name: 'companyNumber' })
  const watchEmail = useWatch({ control, name: 'email' })
  const watchIsCompany = useWatch({ control, name: 'isCompany' })
  const watchFirstName = useWatch({ control, name: 'firstName' })
  const watchLastName = useWatch({ control, name: 'lastName' })
  const watchMobilePhone = useWatch({ control, name: 'mobilePhone' })

  // i18n
  const { t } = useTranslation(['registration', 'commonFormFields', 'common', 'validation'])

  // Local state
  const [alreadyCustomerModal, setAlreadyCustomerModal] = useState<boolean>(false)
  const [extraAddressModalOpen, setExtraAddressModalOpen] = useState<string | null>(null)
  const [createProspectError, setCreateProspectError] = useState<boolean>(false)
  const [creditCheckError, setCreditCheckError] = useState<boolean>(true)
  const [isAlreadyCustomer, setIsAlreadyCustomer] = useState<boolean>(false)
  const [isAlreadyWaitingForPlenty, setIsAlreadyWaitingForPlenty] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [noFixedPricesError, setNoFixedPricesError] = useState<boolean>(false)

  // Constants
  const productAvailableInRegion = checkProductIsAvailableInRegion(simulation.region, propositions.selectedProposition, availableProducts)
  const isExpressRegistration = inputs.simulation.chosenSimulationType === SIMULATION_TYPES.express
  const isEavOrFixed = checkIsEavOrFixed(inputs.simulation.chosenSimulationType)
  const noSimulation = isEavOrFixed
  const boltFlexDriveNotAvailableForRegion = isBoltDriveFlexFlow && simulation.region !== REGIONS.flanders
  const productNotAvailableForRegion = !productAvailableInRegion || boltFlexDriveNotAvailableForRegion
  const showExtraFields = isExpressRegistration || isEavOrFixed
  const isSalesUserWithValidToken = checkAndHandleSalesUser(partnerID)
  const isItsmeFlow = flowAlternative[FlowAlternatives.ITSME]

  /**
   * Triggered everytime the component unloads
   * Prevents unload if one of the form fields is changed
   */
  useBeforeunload((e) => {
    if (isDirty) e.preventDefault()
  })

  /**
   * Triggered everytime watchCompanyNumber changes
   * Removes credit check error if error is shown to re-enable submit button
   */
  useEffect(() => {
    if (creditCheckError) {
      setCreditCheckError(false)
    }
  }, [watchCompanyNumber])

  /**
   * Triggered everytime isCompany changes
   * Automatically sets subjectToVat to true because most companies are subject to VAT
   * Automatically sets subjectToVat back to undefined when not a company
   */
  useEffect(() => {
    if (watchIsCompany) {
      setValue(PersonalDataFieldKeys.SUBJECT_TO_VAT, true)
    } else {
      setValue(PersonalDataFieldKeys.SUBJECT_TO_VAT, undefined)
    }
  }, [watchIsCompany, setValue])

  /**
   * Triggered everytime isAlreadyCustomer changes
   * Shows the already customer modal if the lead is already a customer
   */
  useEffect(() => {
    if (isAlreadyCustomer) {
      setAlreadyCustomerModal(true)
    }
  }, [isAlreadyCustomer])

  /**
   * Handles the form submit after validation
   *
   * @param {PersonalDataInputs} data
   */
  const onSubmit = async (data: PersonalDataInputs) => {
    const startTime = performance.now()
    const formattedData: PersonalDataInputs = {
      ...data,
      dateOfBirth: data.isCompany ? undefined : convertDisplayDateToISO(data.dateOfBirth)
    }

    setIsLoading(true)
    setPostHogPersonProperties({
      email: data?.[PersonalDataFieldKeys.EMAIL],
      [CommonTrackingParams.IDENTIFICATION_METHOD]: identificationMethod,
      [CommonTrackingParams.CUSTOMER_TYPE]: personalData.isCompany ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential
    })
    setLogUser({ email: data.email, name: `${data.firstName} ${data.lastName}` })
    setLogContext('personal_data_form_input', data)

    // Leave function if producer is over capacity
    if (producerOverCapacity) {
      setIsLoading(false)
      return
    }

    setNoFixedPricesError(false)

    if (inputs.simulation.chosenSimulationType === SIMULATION_TYPES.fixedAmount && (!fixedPrices || fixedPrices.electricity == 0)) {
      setNoFixedPricesError(true)
    } else {
      // Save personal data to the store
      dispatch(
        updateAllInputs({
          inputs: {
            ...inputs,
            personalData: {
              ...inputs.personalData,
              ...formattedData
            }
          }
        })
      )

      // Check if we're in the email confirmation, no EAN or credit check flow & skip customer check and subscribe if so
      if (!emailConfirmationFlow && !noEanFlow && !creditCheckConfirmationFlow) {
        // Check if the lead is already a customer
        const {
          data: {
            data: { exists }
          }
        } = await axios.get<NextApiResponseBody<CustomerExistsResponse>>(`/api/customers/exists?email=${encodeURIComponent(data.email)}`)

        if (exists) {
          if (isSalesUserWithValidToken.authenticated) {
            dispatch(setIsExtraAddressFlow(true))

            // Sales user - check if the sales user has access to one or more accounts with the given email
            const hasAlreadyFetchedAccountsForEmail = extraAddress?.accounts && data.email in extraAddress?.accounts
            const [res] = await Promise.all([
              !hasAlreadyFetchedAccountsForEmail ? partnersLib.getPartnerContacts(partnerID, data.email) : undefined,
              handleEanLookup()
            ])

            // Already fetched this email address - open modal if accounts are found
            if (hasAlreadyFetchedAccountsForEmail) {
              if (extraAddress.accounts[data.email]) {
                setExtraAddressModalOpen(data.email)
              } else {
                addLogBreadcrumb('PersonalData: sales user, no accounts on record for email, advancing')
                nextStep()
              }
            } else {
              if (res?.data?.contacts?.length) {
                // Accounts found - save them to the store & open modal
                dispatch(setExtraAddressAccountsByEmail({ email: data.email, accounts: res.data.contacts[0].accounts }))
                setExtraAddressModalOpen(data.email)
              } else {
                // No accounts found - go to next step
                addLogBreadcrumb('PersonalData: sales user, no partner contacts found, advancing')
                nextStep()
              }
            }
          } else {
            dispatch(setIsExtraAddressFlow(false))

            // Regular user - display already customer modal
            setIsAlreadyCustomer(exists)
          }
        } else {
          dispatch(setIsExtraAddressFlow(false))

          // Subscribe the lead if he/she is not a customer
          if (noSimulation) {
            dispatch(
              updateInput({
                group: InputGroupKeys.SIMULATION,
                key: SimulationFieldKeys.USAGE,
                value: determineSimulationUsage()
              })
            )
          }

          const durationUntilNow = performance.now() - startTime
          const [subscribed] = await Promise.all([handleSubscribe(formattedData, exists, durationUntilNow), handleEanLookup()])

          if (subscribed) {
            // Go to the next registration step
            addLogBreadcrumb('PersonalData: lead subscribed, advancing')
            nextStep()
          }
        }

        // Check if we're in the noEan flow & change eanCaptureType to 'No EAN process'
      } else if (noEanFlow) {
        dispatch(
          updateInput({
            group: InputGroupKeys.METER_DETAILS,
            key: MeterDetailsFieldKeys.EAN_CAPTURE_TYPE,
            value: EAN_CAPTURE_TYPES.noEanProcess
          })
        )

        // Go to the next registration step.
        addLogBreadcrumb('PersonalData: noEanFlow, advancing')
        nextStep()
      } else {
        // Go to the next registration step.
        addLogBreadcrumb('PersonalData: emailConfirmationFlow, advancing')
        nextStep()
      }
    }

    setIsLoading(false)
  }

  /**
   * Handles the company checkbox change
   * @param {boolean} isCompany
   */
  const handleIsCompanyChange = (isCompany: boolean) => {
    if (isCompany && identificationMethod === IDENTIFICATION_METHODS.itsme) {
      dispatch(updateMarketingData({ key: MarketingDataKeys.IDENTIFICATION_METHOD, value: IDENTIFICATION_METHODS.default }))
    }

    // ItsMe is temporary disable due to acquisition block project.
    // if (!isCompany) {
    //   const isItsmeFlow = checkShouldIdentifyWithItsme(simulation.region, personalData.isCompany, marketingStore)
    //   dispatch(
    //     updateMarketingData({
    //       key: MarketingDataKeys.IDENTIFICATION_METHOD,
    //       value: isItsmeFlow ? IDENTIFICATION_METHODS.itsme : IDENTIFICATION_METHODS.default
    //     })
    //   )
    // }

    if (noSimulation) {
      dispatch(updateInput({ group: InputGroupKeys.PERSONAL_DATA, key: PersonalDataFieldKeys.IS_COMPANY, value: isCompany }))
    } else {
      if (simulation.usage.grade !== USAGE_GRADES.custom) {
        dispatch(
          updateInput({
            group: InputGroupKeys.SIMULATION,
            key: SimulationFieldKeys.USAGE,
            value: {
              ...simulation.usage,
              grade: isCompany ? USAGE_GRADES.low : USAGE_GRADES.average
            }
          })
        )
      }

      saveAndResimulate(InputGroupKeys.PERSONAL_DATA, PersonalDataFieldKeys.IS_COMPANY, isCompany)
    }
  }

  /**
   * Handles the EAN lookup flow
   */
  const handleEanLookup = async () => {
    const address: Address = {
      ...deliveryAddress.address,
      countryCode: 'BE'
    }

    try {
      const {
        data: {
          data: { electricity, gas }
        }
      } = await axios.post<NextApiResponseBody<EanLookupResponseBody>>('/api/market-message/preswitch/ean-lookup', {
        language: locale as Language,
        address
      })

      dispatch(
        updateInput({
          group: InputGroupKeys.METER_DETAILS,
          key: MeterDetailsFieldKeys.ELECTRICITY,
          value: {
            ...inputs.meterDetails.electricity,
            ean: electricity
          }
        })
      )

      if (deliveryAddress.needsGas) {
        dispatch(
          updateInput({
            group: InputGroupKeys.METER_DETAILS,
            key: MeterDetailsFieldKeys.GAS,
            value: {
              ...inputs.meterDetails.gas,
              ean: gas
            }
          })
        )
      }

      dispatch(
        updateInput({
          group: InputGroupKeys.METER_DETAILS,
          key: MeterDetailsFieldKeys.EAN_CAPTURE_TYPE,
          value: EAN_CAPTURE_TYPES.prefilled
        })
      )
    } catch (_) {
      dispatch(
        updateInput({
          group: InputGroupKeys.METER_DETAILS,
          key: MeterDetailsFieldKeys.EAN_CAPTURE_TYPE,
          value: EAN_CAPTURE_TYPES.notFound
        })
      )
    }
  }

  /**
   * Handles the subscribe flow
   *
   * @param {PersonalDataInputs} data
   * @param {boolean} exists
   * @param {number} durationUntilNow
   * @return {Promise<boolean>}
   */
  const handleSubscribe = async (data: PersonalDataInputs, exists: boolean, durationUntilNow: number): Promise<boolean> => {
    const startTime = performance.now()
    setCreateProspectError(false)

    try {
      // Parse the subscribe data
      const parsedSubscribeData = parseCreateProspectData({
        language: data.language,
        producer,
        startedFulfillmentFlow: true
      })

      // Create the prospect & check if a response was given
      const { success, data: createProspectData, message } = await boltBackend.upsertProspect(parsedSubscribeData)

      if (!success) {
        throw new Error(message ?? 'Create prospect failed!')
      }

      // Destructure the response
      const {
        opportunityId: returnedOpportunityId,
        creditCheckAccepted,
        accountId,
        alreadyCustomer,
        alreadyAwaitingPlentyInvestment
      } = createProspectData

      // Show already customer modal
      if (message === ERROR_CODES.alreadyCustomer || alreadyCustomer) {
        setIsAlreadyCustomer(true)
        return
      }

      // Show already awaiting Plenty investment modal
      if (alreadyAwaitingPlentyInvestment) {
        setIsAlreadyWaitingForPlenty(true)
        return
      }

      // If there is a conflict between exists endpoint & upsert alreadyCustomer, show common error and early return
      if (!exists && !returnedOpportunityId) {
        log({
          error: 'No opportunity ID returned from the upsertProspect call while the customer exists returned false.',
          severity: Severity.ERROR,
          identifier: `[${Flow.REGISTRATION}:upsertProspect]`,
          criticality: Criticality.HIGH
        })
        setCreateProspectError(true)
        return
      }

      // Check if an opportunity id has been returned
      if (
        (!personalData[PersonalDataFieldKeys.IS_COMPANY] && !!returnedOpportunityId) ||
        (personalData[PersonalDataFieldKeys.IS_COMPANY] && !!returnedOpportunityId && !executeCreditCheck) ||
        (personalData[PersonalDataFieldKeys.IS_COMPANY] && !!returnedOpportunityId && executeCreditCheck && creditCheckAccepted)
      ) {
        // Set subscription as successful & save the returned opportunity id
        dispatch(updateOpportunityId({ opportunityId: returnedOpportunityId }))
        dispatch(updateAccountId(accountId))
        identifyUser(returnedOpportunityId, {
          email: data.email,
          [CommonTrackingParams.IDENTIFICATION_METHOD]: identificationMethod,
          [CommonTrackingParams.CUSTOMER_TYPE]: personalData.isCompany ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential
        })

        const totalDuration = performance.now() - startTime + durationUntilNow
        // Track 'Subscription step finished' event @Semetis
        trackGTMEvent('subscription_step_finished:personal-data', {
          account_id: createProspectData?.accountId || '',
          bolt_product: propositions.selectedProposition,
          costumer_type: personalData.isCompany ? 'company' : 'private',
          email: data.email,
          postcode: deliveryAddress.address.postalCode,
          producer_name: producer.name[locale],
          duration_ms: totalDuration
        } satisfies EventTrackingParameters)
        trackEvent(
          { category: 'subscription', object: 'personal_data_step', action: 'finish' },
          {
            [CommonTrackingParams.IDENTIFICATION_METHOD]: identificationMethod,
            [CommonTrackingParams.DURATION]: totalDuration
          }
        )

        return true
      } else {
        if (personalData[PersonalDataFieldKeys.IS_COMPANY] && executeCreditCheck && createProspectData?.creditCheckAccepted === false) {
          setCreditCheckError(true)
          window.scrollTo(0, 0) // Scroll to top
          setIsLoading(false)
        }

        if (
          personalData[PersonalDataFieldKeys.IS_COMPANY] &&
          executeCreditCheck &&
          typeof createProspectData?.creditCheckAccepted === 'undefined'
        ) {
          throw new Error(`Error while checking credit score for ${data.companyNumber} (${data.firstName} ${data.lastName})`) // Error will be caught and sent to Sentry
        }

        return false
      }
    } catch (error) {
      setCreateProspectError(true)

      if ('response' in error && error.response.data?.message === ERROR_CODES.alreadyCustomer) {
        setIsAlreadyCustomer(true)
      } else {
        log({ error, identifier: `[${Flow.REGISTRATION}:createProspect]` })
      }

      return false
    }
  }

  return (
    <FormProvider {...hookForm}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Fieldset title={t('personalData.sections.contact')}>
          {itsme?.prefilled && !!Object.values(itsme.prefilled).some(Boolean) && (
            <p className="text-subdued grid-col-full text-xs">{t('personalData.itsmePrefilledMessage')}</p>
          )}

          {showExtraFields && (
            <Checkbox
              {...register('isCompany', {
                onChange: (e) => handleIsCompanyChange(e.target.checked)
              })}
              className="grid-col-full"
              id="isCompany"
              label={t('personalData.fields.company.label')}
            />
          )}

          {/* IS COMPANY FIELDS */}
          {watchIsCompany && (
            <>
              <Checkbox
                {...register('subjectToVat')}
                className="grid-col-full"
                id="subjectToVat"
                label={t('personalData.fields.subjectToVat.label')}
              />

              <CompanyName />

              <Select
                {...register('legalForm', {
                  required: t('validation:required')
                })}
                error={errors?.legalForm?.message}
                label={t('personalData.fields.legalForm.label')}
              >
                {Object.values(LEGAL_FORMS)
                  .sort((a, b) => {
                    return t(`common:legalForms.${a}`).localeCompare(t(`common:legalForms.${b}`))
                  })
                  .map((value) => {
                    const label = t(`common:legalForms.${value}`)

                    return (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    )
                  })}
              </Select>

              <CompanyNumber />

              <Form.EmptyField />

              {creditCheckError && <Banner type="blocking">{t('validation:creditCheck')}</Banner>}
            </>
          )}

          <FirstName readonly={isItsmeFlow && itsme?.prefilled?.firstName ? watchFirstName : null} />

          <LastName readonly={isItsmeFlow && itsme?.prefilled?.lastName ? watchLastName : null} />

          <Email
            readonly={
              emailConfirmationFlow || noEanFlow || creditCheckConfirmationFlow || (isItsmeFlow && itsme?.prefilled?.email)
                ? watchEmail
                : null
            }
            {...(isAlreadyCustomer && !alreadyCustomerModal && { errorMessage: t('common:errors.alreadyCustomer') })}
          />

          {!(
            (isItsmeFlow && (itsme?.prefilled?.email || itsme?.overwritten?.email)) ||
            emailConfirmationFlow ||
            noEanFlow ||
            creditCheckConfirmationFlow
          ) && (
            <Email
              isRepeat
              readonly={emailConfirmationFlow || noEanFlow || creditCheckConfirmationFlow ? watchEmail : null}
              {...(isAlreadyCustomer && !alreadyCustomerModal && { errorMessage: t('common:errors.alreadyCustomer') })}
            />
          )}

          <Phone readonly={isItsmeFlow && itsme?.prefilled?.mobilePhone ? watchMobilePhone : null} />

          {!watchIsCompany && <BirthDate />}

          <Select
            {...register('language', {
              required: t('validation:required')
            })}
            error={errors?.language?.message}
            label={t('personalData.fields.language.label')}
          >
            {Object.keys(FullLanguages).map((language) => (
              <option key={language} value={language}>
                {FullLanguages[language]}
              </option>
            ))}
          </Select>
        </Form.Fieldset>

        <Form.Footer>
          <FormButtons {...{ isDisabled: productNotAvailableForRegion, isLoading }} />
          {noFixedPricesError && (
            <Banner className="mt-500" type="blocking">
              {t('validation:noFixedPrices')}
            </Banner>
          )}
          {createProspectError && !creditCheckError && (
            <Banner className="mt-500" type="blocking">
              {t('common:error')}
            </Banner>
          )}
        </Form.Footer>
      </Form>

      <ExtraAddressModal
        customerType={watchIsCompany ? CUSTOMER_TYPES['professional'] : CUSTOMER_TYPES['residential']}
        email={extraAddressModalOpen}
        isOpen={!!extraAddressModalOpen}
        onRequestClose={() => setExtraAddressModalOpen(null)}
        {...{ nextStep }}
      />

      <AlreadyCustomerModal
        email={watchEmail}
        isOpen={alreadyCustomerModal || isAlreadyWaitingForPlenty}
        alreadyWaitingForPlentyInvestment={isAlreadyWaitingForPlenty}
        onRequestClose={() => {
          setAlreadyCustomerModal(false)
          setIsAlreadyWaitingForPlenty(false)
        }}
      />
    </FormProvider>
  )
}

export default PersonalData
